import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import type {
  TechDebtIssue,
  TechDebtIssuesResponse,
} from '@internal/backstage-plugin-techdebt-common';
import type { TechDebtApi } from './TechDebtApi';

type TechDebtClientOptions = {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
};

export class TechDebtClient implements TechDebtApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: TechDebtClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async listIssuesByEntityRef(entityRef: string): Promise<TechDebtIssue[]> {
    const query = encodeURIComponent(entityRef.trim());
    const response = await this.fetchJson<TechDebtIssuesResponse>(
      `/issues?entityRef=${query}`,
    );
    return response.items;
  }

  async createIssue(input: TechDebtIssue): Promise<TechDebtIssue> {
    return this.fetchJson<TechDebtIssue>('/issues', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  private async fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('techdebt');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }
}
